import React, { useState, useEffect } from 'react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, Cell,
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Bell, Settings, User, Users, HelpCircle,  Activity, Calendar, PieChart as PieChartIcon, BarChart2, MessageSquare, Eye, LogOut } from 'lucide-react';
import SiteVisitAnalysis from './SiteVisit_Analytics';
import UserSupport from './SupportDashboard';
import TeamBio from './TeamBio';

// Sample data - would be fetched from an API in a real application
const generateDailyData = () => {
  const data = [];
  for (let i = 0; i < 30; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    data.unshift({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visitors: Math.floor(Math.random() * 1000) + 500,
      newUsers: Math.floor(Math.random() * 200) + 50,
      pageViews: Math.floor(Math.random() * 5000) + 1000,
      interactions: Math.floor(Math.random() * 300) + 100,
    });
  }
  return data;
};

const generateUserData = () => {
  const users = [];
  const statuses = ['Online', 'Offline', 'Away'];
  const names = [
    'John Smith', 'Emma Johnson', 'Michael Williams', 'Sophia Brown', 
    'William Jones', 'Olivia Davis', 'James Miller', 'Isabella Wilson', 
    'Robert Moore', 'Mia Taylor', 'David Anderson', 'Charlotte Thomas'
  ];
  
  for (let i = 0; i < 50; i++) {
    users.push({
      id: i + 1,
      name: names[i % names.length],
      email: `${names[i % names.length].toLowerCase().replace(' ', '.')}@example.com`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      lastActive: new Date(Date.now() - Math.floor(Math.random() * 10) * 86400000).toLocaleDateString(),
      registrationDate: new Date(Date.now() - Math.floor(Math.random() * 365) * 86400000).toLocaleDateString(),
      interactions: Math.floor(Math.random() * 100),
      subscription: Math.random() > 0.3 ? 'Premium' : 'Basic',
    });
  }
  return users;
};

const generateContactData = () => {
  const contacts = [];
  const statuses = ['Pending', 'Resolved', 'In Progress'];
  const subjects = [
    'Account Issue', 'Payment Problem', 'Feature Request', 'Bug Report', 
    'General Inquiry', 'Subscription Question', 'Technical Support', 'Feedback'
  ];
  
  for (let i = 0; i < 30; i++) {
    contacts.push({
      id: i + 1,
      name: `User ${i + 1}`,
      email: `user${i + 1}@example.com`,
      subject: subjects[Math.floor(Math.random() * subjects.length)],
      message: `This is a sample message content for ticket #${i + 1}. It would contain the user's inquiry or issue description.`,
      date: new Date(Date.now() - Math.floor(Math.random() * 30) * 86400000).toLocaleDateString(),
      status: statuses[Math.floor(Math.random() * statuses.length)],
      priority: Math.floor(Math.random() * 3) + 1,
    });
  }
  return contacts;
};

const pieData = [
  { name: 'Desktop', value: 65 },
  { name: 'Mobile', value: 30 },
  { name: 'Tablet', value: 5 },
];

const userTypeData = [
  { name: 'Premium Users', value: 35 },
  { name: 'Basic Users', value: 55 },
  { name: 'Trial Users', value: 10 },
];

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dailyData, setDailyData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [contactData, setContactData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [notifications, setNotifications] = useState(5);

  // Simulate data loading
  useEffect(() => {
    setDailyData(generateDailyData());
    setUserData(generateUserData());
    setContactData(generateContactData());
  }, []);

  // Filter users based on search term
  const filteredUsers = userData.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter contacts based on search term
  const filteredContacts = contactData.filter(contact => 
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gray-800 text-white">
        <div className="p-4 border-b border-gray-700">
          <h1 className="text-xl font-bold">Admin Portal</h1>
          <p className="text-xs text-gray-400">Super Admin Panel</p>
        </div>
        <nav className="p-4">
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'dashboard' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
          >
            <Activity size={18} className="mr-2" />
            Dashboard
          </button>
          <button 
              onClick={() => setActiveTab('site-visits')}
              className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'site-visits' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            >
              <Eye size={18} className="mr-2" />
              Site Visit Analysis
            </button>

            {/* New User Support Button */}
            <button 
              onClick={() => setActiveTab('support')}
              className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'support' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            >
              <HelpCircle size={18} className="mr-2" />
              User Support
            </button>
          <button 
            onClick={() => setActiveTab('users')}
            className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'users' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
          >
            <Users size={18} className="mr-2" />
            Users
          </button>
          <button 
            onClick={() => setActiveTab('teambio')}
            className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'teambio' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
          >
            <Users size={18} className="mr-2" />
            Team Bio
          </button>
          <button 
            onClick={() => setActiveTab('contacts')}
            className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'contacts' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
          >
            <MessageSquare size={18} className="mr-2" />
            Contact Requests
          </button>
          <button 
            onClick={() => setActiveTab('statistics')}
            className={`flex items-center w-full p-2 mb-2 rounded ${activeTab === 'statistics' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
          >
            <BarChart2 size={18} className="mr-2" />
            Statistics
          </button>
          <div className="mt-8 pt-4 border-t border-gray-700">
            <button className="flex items-center w-full p-2 mb-2 rounded hover:bg-gray-700">
              <Settings size={18} className="mr-2" />
              Settings
            </button>
            <button className="flex items-center w-full p-2 mb-2 rounded hover:bg-gray-700">
              <LogOut size={18} className="mr-2" />
              Logout
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-x-hidden overflow-y-auto">
        {/* Header */}
        <header className="bg-white shadow p-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {activeTab === 'dashboard' && 'Dashboard Overview'}
              {activeTab === 'site-visits' && 'Site Visit Analysis'}
              {activeTab === 'support' && 'User Support'}
              {activeTab === 'teambio' && 'Team Bio'}
              {activeTab === 'users' && 'User Management'}
              {activeTab === 'contacts' && 'Contact Requests'}
              {activeTab === 'statistics' && 'Analytics & Statistics'}
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <button className="p-2 rounded-full hover:bg-gray-100 relative">
                <Bell size={20} />
                {notifications > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>
            </div>
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white mr-2">
                SA
              </div>
              <span className="text-sm font-medium">Super Admin</span>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-6">
          {activeTab === 'dashboard' && (
            <>
              {/* Top Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 text-blue-500 mr-4">
                      <Eye size={24} />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Total Visitors</p>
                      <h2 className="text-2xl font-bold">24,512</h2>
                      <p className="text-green-500 text-xs">+12.5% from last month</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 text-green-500 mr-4">
                      <User size={24} />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Total Users</p>
                      <h2 className="text-2xl font-bold">8,342</h2>
                      <p className="text-green-500 text-xs">+8.2% from last month</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 text-purple-500 mr-4">
                      <MessageSquare size={24} />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Contact Requests</p>
                      <h2 className="text-2xl font-bold">652</h2>
                      <p className="text-red-500 text-xs">+24.8% from last month</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-yellow-100 text-yellow-500 mr-4">
                      <Activity size={24} />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Conversion Rate</p>
                      <h2 className="text-2xl font-bold">5.27%</h2>
                      <p className="text-green-500 text-xs">+1.2% from last month</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Range Selector */}
              <div className="flex justify-end mb-4">
                <div className="inline-flex rounded-md shadow-sm">
                  <button
                    onClick={() => setSelectedTimeRange('7d')}
                    className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                      selectedTimeRange === '7d' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    7 Days
                  </button>
                  <button
                    onClick={() => setSelectedTimeRange('30d')}
                    className={`px-4 py-2 text-sm font-medium ${
                      selectedTimeRange === '30d' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    30 Days
                  </button>
                  <button
                    onClick={() => setSelectedTimeRange('90d')}
                    className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                      selectedTimeRange === '90d' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    90 Days
                  </button>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">Visitor Traffic</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="visitors" stackId="1" stroke="#8884d8" fill="#8884d8" />
                      <Area type="monotone" dataKey="pageViews" stackId="2" stroke="#82ca9d" fill="#82ca9d" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">User Growth</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="newUsers" stroke="#8884d8" activeDot={{ r: 8 }} />
                      <Line type="monotone" dataKey="interactions" stroke="#82ca9d" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">Device Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#8884d8', '#82ca9d', '#ffc658'][index % 3]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">User Types</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={userTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {userTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#ff8042', '#0088fe', '#00C49F'][index % 3]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white mr-3">
                        <User size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">New user registered</p>
                        <p className="text-xs text-gray-500">15 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white mr-3">
                        <MessageSquare size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">New contact request</p>
                        <p className="text-xs text-gray-500">32 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white mr-3">
                        <User size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">User upgraded to premium</p>
                        <p className="text-xs text-gray-500">1 hour ago</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center text-white mr-3">
                        <MessageSquare size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Support ticket resolved</p>
                        <p className="text-xs text-gray-500">2 hours ago</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'teambio' && (
              <div>                
                <TeamBio />
              </div>
            )}


        {activeTab === 'site-visits' && (
              <div>
                {/* Site Visit Analysis Component */}
                <SiteVisitAnalysis />
              </div>
            )}

            {activeTab === 'support' && (
              <div>
                {/* User Support Component */}
                <UserSupport />
              </div>
            )}

          {activeTab === 'users' && (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h3 className="text-lg font-semibold">User Management</h3>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search users..."
                      className="px-4 py-2 border rounded-lg w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                      <BarChart2 size={18} />
                    </button>
                    <button 
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                      <PieChartIcon size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {viewMode === 'table' ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.slice(0, 10).map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.id}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0 mr-2 bg-gray-100 rounded-full flex items-center justify-center">
                                {user.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.status === 'Online' 
                                ? 'bg-green-100 text-green-800' 
                                : user.status === 'Away' 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.subscription === 'Premium' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {user.subscription}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.lastActive}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                            <button className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        Previous
                      </button>
                      <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">1</span> to <span className="font-medium">10</span> of{' '}
                          <span className="font-medium">{filteredUsers.length}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                            Previous
                          </button>
                          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                            1
                          </button>
                          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                            2
                          </button>
                          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                            3
                          </button>
                          <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredUsers.slice(0, 9).map((user) => (
                    <div key={user.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4 text-lg font-semibold">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <h4 className="text-lg font-medium">{user.name}</h4>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <div>
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.status === 'Online' 
                              ? 'bg-green-100 text-green-800' 
                              : user.status === 'Away' 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Subscription</p>
                          <p className="text-sm font-medium">{user.subscription}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Interactions</p>
                          <p className="text-sm font-medium">{user.interactions}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Last Active</p>
                          <p className="text-sm font-medium">{user.lastActive}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Registered</p>
                          <p className="text-sm font-medium">{user.registrationDate}</p>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t flex justify-end space-x-2">
                        <button className="px-3 py-1 text-sm rounded border border-blue-500 text-blue-500 hover:bg-blue-50">
                          Edit
                        </button>
                        <button className="px-3 py-1 text-sm rounded border border-red-500 text-red-500 hover:bg-red-50">
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'contacts' && (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h3 className="text-lg font-semibold">Contact Requests</h3>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      className="px-4 py-2 border rounded-lg w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <select className="px-4 py-2 border rounded-lg text-sm">
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="in-progress">In Progress</option>
                    </select>
                    <select className="px-4 py-2 border rounded-lg text-sm">
                      <option value="all">All Priorities</option>
                      <option value="1">High</option>
                      <option value="2">Medium</option>
                      <option value="3">Low</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredContacts.slice(0, 10).map((contact) => (
                      <tr key={contact.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                            <div className="text-sm text-gray-500 ml-1">({contact.email})</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.subject}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            contact.status === 'Resolved' 
                              ? 'bg-green-100 text-green-800' 
                              : contact.status === 'Pending' 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {contact.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            contact.priority === 1 
                              ? 'bg-red-100 text-red-800' 
                              : contact.priority === 2 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {contact.priority === 1 ? 'High' : contact.priority === 2 ? 'Medium' : 'Low'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 mr-3">View</button>
                          <button className="text-green-600 hover:text-green-900 mr-3">Resolve</button>
                          <button className="text-red-600 hover:text-red-900">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">1</span> to <span className="font-medium">10</span> of{' '}
                        <span className="font-medium">{filteredContacts.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                          Previous
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-blue-50 text-sm font-medium text-blue-600 hover:bg-blue-100">
                          1
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                          2
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                          3
                        </button>
                        <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'statistics' && (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h3 className="text-lg font-semibold">Advanced Analytics & Statistics</h3>
                <div className="flex items-center space-x-4">
                  <div className="inline-flex rounded-md shadow-sm">
                    <button
                      onClick={() => setSelectedTimeRange('7d')}
                      className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                        selectedTimeRange === '7d' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      7 Days
                    </button>
                    <button
                      onClick={() => setSelectedTimeRange('30d')}
                      className={`px-4 py-2 text-sm font-medium ${
                        selectedTimeRange === '30d' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      30 Days
                    </button>
                    <button
                      onClick={() => setSelectedTimeRange('90d')}
                      className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                        selectedTimeRange === '90d' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      90 Days
                    </button>
                  </div>
                  <button className="px-4 py-2 border rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Export Data
                  </button>
                </div>
              </div>

              {/* User Engagement Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">User Engagement by Feature</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      {name: 'Dashboard', value: 78.5},
                      {name: 'Reports', value: 65.2},
                      {name: 'Analytics', value: 52.8},
                      {name: 'Settings', value: 42.3},
                      {name: 'Profiles', value: 38.7},
                      {name: 'Messages', value: 35.1},
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis label={{ value: 'Usage %', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">Session Duration Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyData.map(item => ({
                      date: item.date,
                      duration: Math.floor(Math.random() * 10) + 5
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="duration" stroke="#82ca9d" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Conversion Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">Conversion Funnel</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart layout="vertical" data={[
                      {name: 'Visitors', value: 100},
                      {name: 'Sign-ups', value: 42},
                      {name: 'Active Users', value: 28},
                      {name: 'Paying Users', value: 12},
                      {name: 'Retained', value: 8},
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8">
                        {[
                          {name: 'Visitors', value: 100},
                          {name: 'Sign-ups', value: 42},
                          {name: 'Active Users', value: 28},
                          {name: 'Paying Users', value: 12},
                          {name: 'Retained', value: 8},
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c'][index % 5]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">Geographical Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          {name: 'North America', value: 42},
                          {name: 'Europe', value: 28},
                          {name: 'Asia', value: 18},
                          {name: 'South America', value: 8},
                          {name: 'Africa', value: 3},
                          {name: 'Oceania', value: 1},
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {[
                          {name: 'North America', value: 42},
                          {name: 'Europe', value: 28},
                          {name: 'Asia', value: 18},
                          {name: 'South America', value: 8},
                          {name: 'Africa', value: 3},
                          {name: 'Oceania', value: 1},
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'][index % 6]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">System Performance</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Server Load</span>
                        <span className="text-sm font-medium text-gray-700">65%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '65%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Database</span>
                        <span className="text-sm font-medium text-gray-700">82%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '82%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Cache Hit Rate</span>
                        <span className="text-sm font-medium text-gray-700">92%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">API Response</span>
                        <span className="text-sm font-medium text-gray-700">78%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">User Retention</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">After 1 Day</span>
                        <span className="text-sm font-medium text-gray-700">88%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '88%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">After 1 Week</span>
                        <span className="text-sm font-medium text-gray-700">72%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '72%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">After 1 Month</span>
                        <span className="text-sm font-medium text-gray-700">53%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '53%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">After 3 Months</span>
                        <span className="text-sm font-medium text-gray-700">31%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '31%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">Conversion Rates</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Visitor to Signup</span>
                        <span className="text-sm font-medium text-gray-700">12.5%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-purple-600 h-2.5 rounded-full" style={{ width: '12.5%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Signup to Active</span>
                        <span className="text-sm font-medium text-gray-700">68.2%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-purple-600 h-2.5 rounded-full" style={{ width: '68.2%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Free to Paid</span>
                        <span className="text-sm font-medium text-gray-700">5.7%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-purple-600 h-2.5 rounded-full" style={{ width: '5.7%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">Churn Rate</span>
                        <span className="text-sm font-medium text-gray-700">3.2%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-red-600 h-2.5 rounded-full" style={{ width: '3.2%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Options */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Export Analytics Data</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button className="flex items-center justify-center p-4 border rounded-lg hover:bg-gray-50">
                    <div className="mr-2">📊</div>
                    <div>
                      <p className="font-medium">Excel Export</p>
                      <p className="text-sm text-gray-500">Download .xlsx</p>
                    </div>
                  </button>
                  <button className="flex items-center justify-center p-4 border rounded-lg hover:bg-gray-50">
                    <div className="mr-2">📈</div>
                    <div>
                      <p className="font-medium">PDF Report</p>
                      <p className="text-sm text-gray-500">Download .pdf</p>
                    </div>
                  </button>
                  <button className="flex items-center justify-center p-4 border rounded-lg hover:bg-gray-50">
                    <div className="mr-2">📋</div>
                    <div>
                      <p className="font-medium">CSV Export</p>
                      <p className="text-sm text-gray-500">Download .csv</p>
                    </div>
                  </button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;